import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Pill, TrendingUp, Clock, ArrowRight, X } from 'lucide-react';
import { MemberContext } from '../App';
import axios from 'axios';

const DrugSearch = () => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { rxgoMemberId, setRxgoMemberId } = useContext(MemberContext);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const colors = {
    primary: '#2055df',
    primaryDark: '#1a44b8',
    secondary: '#0f172a',
    accent: '#06b6d4',
    success: '#10b981',
    bg: '#f8fafc',
    white: '#ffffff',
    gray100: '#f1f5f9',
    gray200: '#e2e8f0',
    gray400: '#94a3b8',
    gray500: '#64748b',
    gray600: '#475569',
    gray800: '#1e293b',
    gray900: '#0f172a'
  };

  const popularDrugs = [
    'Lisinopril', 'Metformin', 'Atorvastatin', 'Amlodipine', 
    'Omeprazole', 'Losartan', 'Gabapentin', 'Levothyroxine'
  ];

  useEffect(() => {
    const saved = localStorage.getItem('rxRecentSearches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (e) {}
    }
  }, []);

  useEffect(() => {
    // Auto-focus only on desktop
    if (!isMobile && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isMobile]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.length >= 2) {
        searchDrugs(query);
      } else {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const searchDrugs = async (searchQuery) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`/api/drugs/search/${encodeURIComponent(searchQuery)}`, {
        headers: { 'rxgo-member-id': rxgoMemberId }
      });
      setSuggestions(response.data.drugs || []);
      if (response.data.memberId) setRxgoMemberId(response.data.memberId);
    } catch (error) {
      console.error('Search error:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectDrug = (drug) => {
    const drugName = typeof drug === 'string' ? drug : (drug.uri || drug.name);
    
    // Save to recent searches
    const updated = [drugName, ...recentSearches.filter(d => d.toLowerCase() !== drugName.toLowerCase())].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('rxRecentSearches', JSON.stringify(updated));
    
    navigate(`/drug/${encodeURIComponent(drugName.toLowerCase())}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      handleSelectDrug(query.trim());
    }
  };

  const clearRecent = () => {
    setRecentSearches([]);
    localStorage.removeItem('rxRecentSearches');
  };

  return (
    <div style={{ 
      fontFamily: "'Inter', -apple-system, sans-serif", 
      background: colors.bg, 
      minHeight: '100vh' 
    }}>
      {/* Hero Section - NO WHITE GAP */}
      <section style={{
        background: colors.primary,
        padding: isMobile ? '48px 16px 56px' : '80px 24px 100px',
        textAlign: 'center',
        marginTop: 0
      }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h1 style={{ 
            fontSize: isMobile ? 28 : 48, 
            fontWeight: 800, 
            color: 'white', 
            marginBottom: 14,
            lineHeight: 1.2
          }}>
            Search for Your Medication
          </h1>
          <p style={{ 
            fontSize: isMobile ? 15 : 20, 
            color: 'rgba(255,255,255,0.85)', 
            marginBottom: isMobile ? 28 : 40,
            lineHeight: 1.5
          }}>
            Compare prices and save up to 80% at pharmacies near you
          </p>

          {/* Search Box - MOBILE RESPONSIVE */}
          <form onSubmit={handleSubmit}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              background: 'white',
              borderRadius: 16,
              padding: 12,
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: colors.gray100,
                borderRadius: 10,
                padding: '0 12px'
              }}>
                <Search size={20} style={{ color: colors.gray400, flexShrink: 0 }} />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Enter medication name..."
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    fontSize: 16,
                    padding: '14px 0',
                    background: 'transparent',
                    color: colors.gray800,
                    minWidth: 0
                  }}
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: colors.gray400,
                      padding: 4,
                      display: 'flex'
                    }}
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
              <button
                type="submit"
                style={{
                  padding: '14px 20px',
                  background: colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  width: '100%'
                }}
              >
                Search Prices <ArrowRight size={18} />
              </button>
            </div>
          </form>

          {/* Suggestions Dropdown */}
          {suggestions.length > 0 && (
            <div style={{
              background: 'white',
              borderRadius: 12,
              marginTop: 8,
              boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
              maxHeight: 300,
              overflow: 'auto',
              textAlign: 'left'
            }}>
              {suggestions.map((drug, i) => (
                <button
                  key={i}
                  onClick={() => handleSelectDrug(drug)}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: 'none',
                    background: 'transparent',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    borderBottom: `1px solid ${colors.gray100}`
                  }}
                >
                  <Pill size={18} style={{ color: colors.primary, flexShrink: 0 }} />
                  <span style={{ color: colors.gray800, fontWeight: 500, fontSize: 15 }}>
                    {drug.name}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Quick Access Section */}
      <section style={{ padding: isMobile ? '36px 16px' : '60px 24px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          {/* Recent Searches */}
          {recentSearches.length > 0 && (
            <div style={{ marginBottom: isMobile ? 36 : 48 }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: 16 
              }}>
                <h2 style={{ 
                  fontSize: isMobile ? 18 : 20, 
                  fontWeight: 600, 
                  color: colors.gray900, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 8 
                }}>
                  <Clock size={isMobile ? 18 : 20} style={{ color: colors.gray400 }} />
                  Recent Searches
                </h2>
                <button
                  onClick={clearRecent}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: colors.gray500,
                    fontSize: 14,
                    cursor: 'pointer'
                  }}
                >
                  Clear
                </button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {recentSearches.map((drug, i) => (
                  <button
                    key={i}
                    onClick={() => handleSelectDrug(drug)}
                    style={{
                      padding: isMobile ? '10px 16px' : '10px 18px',
                      background: 'white',
                      border: `1px solid ${colors.gray200}`,
                      borderRadius: 8,
                      color: colors.gray800,
                      fontSize: isMobile ? 14 : 14,
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    {drug}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Popular Medications */}
          <div>
            <h2 style={{ 
              fontSize: isMobile ? 18 : 20, 
              fontWeight: 600, 
              color: colors.gray900, 
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <TrendingUp size={isMobile ? 18 : 20} style={{ color: colors.primary }} />
              Popular Medications
            </h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {popularDrugs.map((drug, i) => (
                <button
                  key={i}
                  onClick={() => handleSelectDrug(drug)}
                  style={{
                    padding: isMobile ? '10px 16px' : '10px 18px',
                    background: `${colors.primary}08`,
                    border: `1px solid ${colors.primary}20`,
                    borderRadius: 8,
                    color: colors.primary,
                    fontSize: isMobile ? 14 : 14,
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  {drug}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Help Section */}
      <section style={{ 
        background: 'white', 
        padding: isMobile ? '40px 16px' : '60px 24px', 
        textAlign: 'center' 
      }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{ 
            fontSize: isMobile ? 20 : 24, 
            fontWeight: 700, 
            color: colors.gray900, 
            marginBottom: 12 
          }}>
            Can't Find Your Medication?
          </h2>
          <p style={{ 
            fontSize: isMobile ? 15 : 16, 
            color: colors.gray600, 
            marginBottom: 24,
            lineHeight: 1.6
          }}>
            Try searching by the generic name or check our list of top prescriptions
          </p>
          <button
            onClick={() => navigate('/top-prescriptions')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: isMobile ? '14px 24px' : '14px 28px',
              background: colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: 10,
              fontSize: isMobile ? 15 : 16,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            View Top 50 Medications <ArrowRight size={18} />
          </button>
        </div>
      </section>
    </div>
  );
};

export default DrugSearch;